import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  CAlert,
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CFormCheck,
  CListGroup,
  CListGroupItem,
  CRow,
  CSpinner,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cibFacebook, cibWhatsapp, cilCheckCircle, cilPhone, cilWarning } from "@coreui/icons";
import { useNavigate } from "react-router-dom";
import * as MarketsmsService from "../../services/MarketsmsService";
import { env } from "../../Utils/Environment";
import { WhatsappIntegrationPhoneModel } from "../../models/WhatsappNumbers";

/**
 * Integración de WhatsApp vía Embedded Signup de Meta.
 *
 * Modo "coexistence": el cliente conecta el número que YA usa en la app WhatsApp Business.
 * Sigue usando la app en su celular y MarketSMS envía campañas por Cloud API con el mismo número.
 *
 * Modo "new": número nuevo que solo se usará por Cloud API (no puede estar en ninguna app).
 *
 * Flujo:
 *  1. FB.login(config_id, response_type=code) -> devuelve `code` en el callback.
 *  2. window "message" (WA_EMBEDDED_SIGNUP) -> devuelve waba_id (+ phone_number_id / business_id).
 *  3. Cuando tenemos ambos, se envían al backend (/whatsapp/integrate), que intercambia el code
 *     por el token del cliente, suscribe webhooks y (en coexistencia) sincroniza contactos e historial.
 */

type Mode = "coexistence" | "new";
type Status = "idle" | "loading_sdk" | "waiting_meta" | "processing" | "success" | "error";

interface SessionInfo {
  waba_id: string;
  phone_number_id?: string | null;
  business_id?: string | null;
}

const META_APP_ID = env("META_APP_ID", "307734595235971");
const META_CONFIG_ID = env("META_CONFIG_ID", "496637072717970");
const META_GRAPH_VERSION = env("META_GRAPH_VERSION", "v23.0");

const FINISH_EVENTS = ["FINISH", "FINISH_ONLY_WABA", "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING"];

export const WhatsappIntegration: React.FC = () => {
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>("coexistence");
  const [status, setStatus] = useState<Status>("loading_sdk");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [phones, setPhones] = useState<WhatsappIntegrationPhoneModel[]>([]);
  const [requirementsAccepted, setRequirementsAccepted] = useState(false);

  // El code y la sesión llegan por canales distintos y en cualquier orden
  const codeRef = useRef<string | null>(null);
  const sessionRef = useRef<SessionInfo | null>(null);
  const modeRef = useRef<Mode>(mode);
  const sentRef = useRef(false);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  const finishIntegration = useCallback(async () => {
    if (sentRef.current || !codeRef.current || !sessionRef.current) {
      return;
    }
    sentRef.current = true;
    setStatus("processing");

    try {
      const response = await MarketsmsService.integrateCommerce({
        code: codeRef.current,
        waba_id: sessionRef.current.waba_id,
        phone_number_id: sessionRef.current.phone_number_id ?? null,
        business_id: sessionRef.current.business_id ?? null,
        coexistence: modeRef.current === "coexistence",
      });

      setWarnings(response?.data?.warnings ?? []);
      setPhones(response?.data?.phone_numbers ?? []);

      if (response?.status === 200) {
        setStatus("success");
      } else {
        const detail = response?.data?.error;
        const detailText = typeof detail === "string" ? detail : detail?.message;
        const step = response?.data?.step ? ` (paso: ${response.data.step})` : "";
        setErrorMessage((detailText ?? response?.message ?? "No fue posible completar la integración.") + step);
        setStatus("error");
      }
    } catch (error) {
      setErrorMessage("Error de comunicación con el servidor. Intenta nuevamente.");
      setStatus("error");
    }
  }, []);

  // Eventos de sesión de Embedded Signup
  const sessionInfoListener = useCallback((event: MessageEvent) => {
    if (!event.origin || !event.origin.endsWith("facebook.com")) {
      return;
    }

    let payload: any;
    try {
      payload = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
    } catch {
      return; // Mensajes que no son JSON
    }

    if (!payload || payload.type !== "WA_EMBEDDED_SIGNUP") {
      return;
    }

    if (FINISH_EVENTS.includes(payload.event)) {
      const data = payload.data ?? {};
      sessionRef.current = {
        waba_id: String(data.waba_id),
        phone_number_id: data.phone_number_id ? String(data.phone_number_id) : null,
        business_id: data.business_id ? String(data.business_id) : null,
      };
      finishIntegration();
    } else if (payload.event === "ERROR") {
      setErrorMessage(payload.data?.error_message ?? "Meta reportó un error durante el registro.");
      setStatus("error");
    } else if (payload.event === "CANCEL") {
      const step = payload.data?.current_step;
      setErrorMessage(step ? `Proceso cancelado en el paso: ${step}` : "Proceso cancelado.");
      setStatus("idle");
    }
  }, [finishIntegration]);

  useEffect(() => {
    const w = window as any;

    const init = () => {
      w.FB.init({
        appId: META_APP_ID,
        autoLogAppEvents: true,
        xfbml: true,
        version: META_GRAPH_VERSION,
      });
      setStatus("idle");
    };

    if (w.FB) {
      init();
    } else {
      w.fbAsyncInit = init;
      if (!document.getElementById("facebook-jssdk")) {
        const js = document.createElement("script");
        js.id = "facebook-jssdk";
        js.async = true;
        js.defer = true;
        js.crossOrigin = "anonymous";
        js.src = "https://connect.facebook.net/es_LA/sdk.js";
        document.body.appendChild(js);
      }
    }

    window.addEventListener("message", sessionInfoListener);
    return () => window.removeEventListener("message", sessionInfoListener);
  }, [sessionInfoListener]);

  const launchWhatsAppSignup = () => {
    const FB = (window as any).FB;
    if (!FB) {
      setErrorMessage("No se pudo cargar el SDK de Facebook. Desactiva bloqueadores de anuncios y recarga la página.");
      setStatus("error");
      return;
    }

    codeRef.current = null;
    sessionRef.current = null;
    sentRef.current = false;
    setErrorMessage(null);
    setWarnings([]);
    setPhones([]);
    setStatus("waiting_meta");

    const extras: any = {
      setup: {},
      sessionInfoVersion: "3",
    };
    if (mode === "coexistence") {
      extras.featureType = "whatsapp_business_app_onboarding";
    }

    // Nota: Meta exige que este callback NO sea async
    FB.login(
      (response: any) => {
        if (response.authResponse && response.authResponse.code) {
          codeRef.current = response.authResponse.code;
          finishIntegration();
          // Si Meta no envía la información de sesión (waba_id), no dejar la pantalla esperando
          setTimeout(() => {
            if (!sessionRef.current && !sentRef.current) {
              setErrorMessage("Meta no devolvió la información de la cuenta de WhatsApp. Intenta de nuevo o contacta a soporte.");
              setStatus("error");
            }
          }, 10000);
        } else {
          setErrorMessage("No se completó la autorización con Facebook.");
          setStatus("idle");
        }
      },
      {
        config_id: META_CONFIG_ID,
        response_type: "code",
        override_default_response_type: true,
        extras,
      }
    );
  };

  const busy = status === "loading_sdk" || status === "waiting_meta" || status === "processing";
  const canStart = !busy && (mode === "new" || requirementsAccepted);

  return (
    <div className="col-12">
      <CCard className="mb-4">
        <CCardHeader>
          <CIcon icon={cibWhatsapp} className="me-2 text-success" />
          <strong>Integración de WhatsApp</strong>
        </CCardHeader>
        <CCardBody>
          <p>
            Conecta tu número de WhatsApp a <strong>MarketSMS</strong> para enviar campañas, plantillas y
            atender conversaciones desde la plataforma.
          </p>

          <h6 className="mt-4 mb-3">1. ¿Qué número vas a conectar?</h6>
          <CRow className="g-3 mb-4">
            <CCol md={6}>
              <CCard
                className={`h-100 ${mode === "coexistence" ? "border-success border-2" : ""}`}
                role="button"
                onClick={() => !busy && setMode("coexistence")}
              >
                <CCardBody>
                  <CFormCheck
                    type="radio"
                    name="integration-mode"
                    id="mode-coexistence"
                    checked={mode === "coexistence"}
                    onChange={() => setMode("coexistence")}
                    disabled={busy}
                    label={<strong>Ya uso WhatsApp Business en mi celular</strong>}
                  />
                  <CBadge color="success" className="ms-4 mb-2">Recomendado · Coexistencia</CBadge>
                  <p className="small text-body-secondary mb-0 ms-4">
                    Conserva tu app WhatsApp Business, tus chats y contactos. Seguirás respondiendo desde el
                    celular y desde MarketSMS podrás enviar campañas con el mismo número.
                  </p>
                </CCardBody>
              </CCard>
            </CCol>
            <CCol md={6}>
              <CCard
                className={`h-100 ${mode === "new" ? "border-primary border-2" : ""}`}
                role="button"
                onClick={() => !busy && setMode("new")}
              >
                <CCardBody>
                  <CFormCheck
                    type="radio"
                    name="integration-mode"
                    id="mode-new"
                    checked={mode === "new"}
                    onChange={() => setMode("new")}
                    disabled={busy}
                    label={<strong>Voy a usar un número nuevo</strong>}
                  />
                  <p className="small text-body-secondary mb-0 ms-4 mt-2">
                    Un número que no esté registrado en WhatsApp ni WhatsApp Business. Solo se podrá usar
                    desde MarketSMS (API).
                  </p>
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>

          {mode === "coexistence" && (
            <>
              <h6 className="mb-3">2. Antes de empezar</h6>
              <CListGroup className="mb-3">
                <CListGroupItem>
                  Tienes la app <strong>WhatsApp Business</strong> (no WhatsApp normal) actualizada a la versión
                  <strong> 2.24.17 o superior</strong>.
                </CListGroupItem>
                <CListGroupItem>
                  Tienes el celular a la mano: durante el proceso Meta mostrará un <strong>código QR</strong> que
                  debes escanear desde la app WhatsApp Business.
                </CListGroupItem>
                <CListGroupItem>
                  Al terminar compartirás tu historial de chats (hasta 6 meses) y contactos. Esto se hace una
                  sola vez y dentro de las primeras 24 horas.
                </CListGroupItem>
                <CListGroupItem>
                  Ten en cuenta: con coexistencia el número envía hasta 20 mensajes por segundo, y algunas funciones de
                  la app (grupos, listas de difusión, llamadas) no se sincronizan con la API.
                </CListGroupItem>
              </CListGroup>
              <CFormCheck
                id="requirements-accepted"
                className="mb-4"
                checked={requirementsAccepted}
                onChange={(e) => setRequirementsAccepted(e.target.checked)}
                disabled={busy}
                label="Cumplo con los requisitos y quiero conectar mi número de WhatsApp Business"
              />
            </>
          )}

          <h6 className="mb-3">{mode === "coexistence" ? "3." : "2."} Conectar con Meta</h6>
          <p className="small text-body-secondary">
            Se abrirá una ventana de Facebook. Inicia sesión con la cuenta que administra tu negocio, selecciona
            o crea tu portafolio comercial y sigue los pasos.
          </p>

          <CButton
            onClick={launchWhatsAppSignup}
            disabled={!canStart}
            className="mb-3"
            style={{ backgroundColor: "#1877f2", borderColor: "#1877f2", color: "#fff", fontWeight: "bold" }}
          >
            {busy ? <CSpinner size="sm" className="me-2" /> : <CIcon icon={cibFacebook} className="me-2" />}
            {status === "loading_sdk" && "Cargando Facebook..."}
            {status === "waiting_meta" && "Completa los pasos en la ventana de Meta..."}
            {status === "processing" && "Configurando tu número..."}
            {!busy && "Continuar con Facebook"}
          </CButton>

          {errorMessage && (
            <CAlert color={status === "error" ? "danger" : "warning"} className="d-flex align-items-center">
              <CIcon icon={cilWarning} className="me-2 flex-shrink-0" />
              <div>{errorMessage}</div>
            </CAlert>
          )}

          {status === "success" && (
            <CAlert color="success">
              <div className="d-flex align-items-center mb-2">
                <CIcon icon={cilCheckCircle} className="me-2" />
                <strong>¡Tu número quedó conectado!</strong>
              </div>
              <ul className="mb-2">
                {phones.map((phone) => (
                  <li key={phone.phone_number_id}>
                    <CIcon icon={cilPhone} size="sm" className="me-1" />
                    +{phone.phone_number} {phone.verified_name ? `· ${phone.verified_name}` : ""}{" "}
                    {phone.coexistence ? (
                      <CBadge color="success">Coexistencia con app</CBadge>
                    ) : (
                      <CBadge color="primary">Solo API</CBadge>
                    )}
                  </li>
                ))}
              </ul>
              <CButton color="success" variant="outline" size="sm" onClick={() => navigate("/whatsapp/numbers")}>
                Ver mis números
              </CButton>
            </CAlert>
          )}

          {warnings.length > 0 && (
            <CAlert color="warning">
              <strong>Atención:</strong>
              <ul className="mb-0">
                {warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </CAlert>
          )}
        </CCardBody>
      </CCard>
    </div>
  );
};
